import { useEffect } from 'react';

interface AutoOpenConfig {
  enabled: boolean;
  delayMs?: number;
  scrollPercent?: number;
  exitIntent?: boolean;
  idleMs?: number;
  maxOpensPerSession?: number;
}

interface UseAutoOpenOptions {
  /** When `open` is controlled externally, skip auto-open entirely */
  controlledOpen: boolean | undefined;
  config: AutoOpenConfig | null | undefined;
  minimized: boolean;
  domainAllowed: boolean;
  openChat: () => void;
  trackAnalytics: (action: string, data: Record<string, unknown>) => void;
}

const STORAGE_KEY = 'rw_auto_open_count';

function getStorageKey(): string {
  if (typeof window === 'undefined') return STORAGE_KEY;
  const host = window.location?.hostname || window.location?.host || 'default';
  return `${STORAGE_KEY}_${host}`;
}

function getCount(): number {
  try {
    const rawHost = window.sessionStorage.getItem(getStorageKey());
    if (rawHost) return Number(rawHost) || 0;
    const rawLegacy = window.sessionStorage.getItem(STORAGE_KEY);
    return rawLegacy ? Number(rawLegacy) || 0 : 0;
  } catch {
    return 0;
  }
}

function incrementCount(): void {
  try {
    const hostKey = getStorageKey();
    const raw = window.sessionStorage.getItem(hostKey);
    const next = (Number(raw) || 0) + 1;
    window.sessionStorage.setItem(hostKey, String(next));
    if (hostKey !== STORAGE_KEY) {
      window.sessionStorage.setItem(STORAGE_KEY, String(next));
    }
  } catch {
    // ignore storage errors
  }
}

export function useAutoOpen({
  controlledOpen,
  config,
  minimized,
  domainAllowed,
  openChat,
  trackAnalytics,
}: UseAutoOpenOptions): void {
  useEffect(() => {
    if (controlledOpen !== undefined) return;
    if (!config || !config.enabled) return;
    if (!minimized) return;
    if (!domainAllowed) return;

    let triggered = false;
    const triggerOpen = (reason: string) => {
      if (triggered) return;
      const count = getCount();
      if (typeof config.maxOpensPerSession === 'number' && count >= config.maxOpensPerSession)
        return;
      triggered = true;
      incrementCount();
      openChat();
      trackAnalytics('auto_open', { reason });
    };

    const cleanups: Array<() => void> = [];

    if (typeof config.delayMs === 'number' && config.delayMs > 0) {
      const id = setTimeout(() => triggerOpen('delay'), config.delayMs);
      cleanups.push(() => clearTimeout(id));
    }

    if (typeof config.scrollPercent === 'number') {
      const handler = () => {
        const doc = document.documentElement;
        const scrollTop = window.pageYOffset || doc.scrollTop || 0;
        const scrollHeight = doc.scrollHeight - doc.clientHeight;
        const percent = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
        if (percent >= config.scrollPercent!) {
          triggerOpen('scroll');
        }
      };
      window.addEventListener('scroll', handler);
      cleanups.push(() => window.removeEventListener('scroll', handler));
    }

    if (config.exitIntent) {
      const handler = (event: MouseEvent) => {
        if (event.clientY <= 0) triggerOpen('exit_intent');
      };
      document.addEventListener('mouseout', handler);
      cleanups.push(() => document.removeEventListener('mouseout', handler));
    }

    if (typeof config.idleMs === 'number' && config.idleMs > 0) {
      let idleTimer: number | undefined;
      const resetTimer = () => {
        if (idleTimer) window.clearTimeout(idleTimer);
        idleTimer = window.setTimeout(() => triggerOpen('idle'), config.idleMs);
      };
      const events: Array<keyof WindowEventMap> = ['mousemove', 'keydown', 'scroll', 'touchstart'];
      events.forEach(evt => window.addEventListener(evt, resetTimer));
      resetTimer();
      cleanups.push(() => {
        events.forEach(evt => window.removeEventListener(evt, resetTimer));
        if (idleTimer) window.clearTimeout(idleTimer);
      });
    }

    return () => {
      cleanups.forEach(fn => fn());
    };
  }, [controlledOpen, config, minimized, domainAllowed, openChat, trackAnalytics]);
}
